// Soft warn lets admins intervene before hard block; 80% threshold matches
// budget UX convention. Cap set per workflow or org-wide; FE only displays.
// Banner is hidden (returns null) in ok state — chip in header is sufficient.
// Warning = non-dismissible (action required); info would be dismissible only.

import { useTranslations } from "use-intl";
import { AlertTriangle, ShieldX, ExternalLink } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { SuggestBanner } from "@/components/ui/suggest-banner/suggest-banner";
import { useCostBudget } from "@/hooks/api/queries/cost-budget.queries";

interface CostBudgetBannerProps {
  resourceType: string;
  resourceId: string;
}

function formatUsd(value: number): string {
  if (value < 0.001) return "<$0.001";
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
}

/**
 * CostBudgetBanner — mounts above DAG canvas on Overview tab.
 * Renders null in ok state; warn (yellow) at ≥80%, danger (red) at ≥100%.
 * Reads from useCostBudget which stubs ok when cost endpoint not yet shipped.
 */
export function CostBudgetBanner({ resourceType, resourceId }: CostBudgetBannerProps) {
  const t = useTranslations("workflows");
  const { data: budget } = useCostBudget({ resourceType, resourceId });

  // ok state or no data → no banner (chip in header is sufficient)
  if (!budget || budget.state === "ok") return null;

  const isBlocked = budget.state === "blocked";
  const currentLabel = formatUsd(budget.currentUsd);
  const capLabel = budget.capUsd != null ? formatUsd(budget.capUsd) : "—";
  const pctLabel = budget.percentUsed != null ? `${Math.round(budget.percentUsed)}%` : "";

  const title = isBlocked
    ? t("cost.banner.blocked", { current: currentLabel, cap: capLabel })
    : t("cost.banner.warn", { current: currentLabel, cap: capLabel, pct: pctLabel });

  const description = isBlocked
    ? t("cost.banner.blocked_desc")
    : t("cost.banner.warn_desc");

  return (
    <SuggestBanner
      tone={isBlocked ? "danger" : "warning"}
      icon={
        isBlocked
          ? <ShieldX size={14} strokeWidth={2} aria-hidden />
          : <AlertTriangle size={14} strokeWidth={2} aria-hidden />
      }
      title={title}
      description={description}
      dismissible={false}
      actions={
        <Link
          to="/usage-logs"
          className="inline-flex items-center gap-1 text-[11px] font-medium underline underline-offset-2 hover:opacity-80"
        >
          <ExternalLink size={11} aria-hidden />
          {t("cost.banner.view_logs")}
        </Link>
      }
    />
  );
}

export default CostBudgetBanner;
